/** @jsxImportSource @emotion/react */

import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { css } from '@emotion/react';
import { Dialog, DialogActions, DialogContent, Divider } from '@mui/material';
import { QSButton, QSTypography } from '../../../../../shared/components/atoms';
import Loader from '../../../../../shared/components/molecules/loader';
import useAddonPurchase from '../../../../../shared/hooks/use-addon-purchase';
import { useCallingAddon } from '../../hooks/use-calling-addon';
import { PlanAddonTypes } from '../../../../renew-plan/v1/typings';
import { FreeTrialStep } from './typings';
import { freeTrialStepAtom } from './states';
import WarningIcon from '../../../../../shared/icons/icons-revamp/warning';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

dayjs.extend(advancedFormat);

export default function TrialEndedModal() {
  const [, setStep] = useAtom(freeTrialStepAtom);
  const { getCallingTrialAddon } = useCallingAddon();

  const onClose = useCallback(() => {
    setStep(FreeTrialStep.Closed);
  }, [setStep]);

  const { purchaseAddon, loading: purchaseLoading } = useAddonPurchase(PlanAddonTypes.CallingLicense, {
    onSuccess: onClose,
  });

  const trialAddon = getCallingTrialAddon();

  const endDateStr = trialAddon?.endDate ? dayjs(trialAddon.endDate).format('Do MMM YYYY') : '—';

  const onBuyLicenses = () => {
    purchaseAddon();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: '8px',
          width: '550px',
          maxWidth: '100%',
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent
        css={css`
          padding: 24px 24px 20px;
          display: flex;
          flex-direction: column;
          align-items: center;
        `}
      >
        <div
          css={css`
            width: 56px;
            height: 56px;
            border-radius: 50%;
            background-color: #ec6a5e1f;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 16px;
          `}
        >
          <WarningIcon
            width={26}
            height={26}
            color="#EC6A5E"
            style={{
              transition: 'translate(2px, 1px)',
            }}
          />
        </div>

        <QSTypography
          css={css`
            font-size: 16px;
            font-weight: 600;
            color: #2d2d2d;
            text-align: center;
            margin-bottom: 8px;
          `}
        >
          Your WhatsApp calling free trial has ended
        </QSTypography>

        <QSTypography
          css={css`
            font-size: 14px;
            font-weight: 400;
            color: #7a7a7a;
            text-align: center;
            line-height: 140%;
          `}
        >
          Your free trial for WhatsApp calling has ended on {endDateStr}.
        </QSTypography>

        <QSTypography
          css={css`
            font-size: 14px;
            font-weight: 400;
            color: #7a7a7a;
            text-align: center;
            line-height: 140%;
          `}
        >
          Your call history is safe. Purchase calling licenses now to continue making and receiving calls.
        </QSTypography>
      </DialogContent>

      <Divider />

      <DialogActions
        css={css`
          padding: 8px 12px;
          display: flex;
          justify-content: flex-end;
          gap: 8px;
        `}
      >
        <QSButton
          css={css`
            height: 40px;
            font-size: 14px;
            background: #fff;
            box-shadow: none;
            color: #2d2d2d;
            border: 1px solid #e0e0e9;
            font-weight: 500;
            :hover {
              background: #f5f5f5;
              box-shadow: none;
            }
          `}
          onClick={onClose}
          disabled={purchaseLoading}
        >
          Cancel
        </QSButton>
        <QSButton
          css={css`
            height: 40px;
            min-width: 160px;
            font-size: 14px;
            font-weight: 600;
          `}
          onClick={onBuyLicenses}
          disabled={purchaseLoading}
        >
          {purchaseLoading ? <Loader size={16} loaderProps={{ sx: { color: 'white' } }} /> : 'Buy calling licenses'}
        </QSButton>
      </DialogActions>
    </Dialog>
  );
}
